import * as React from "react";
import { Button, Radio, Space, Tooltip, notification } from "antd";
import { onError } from "src/common/onError";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { ZUid } from "src/types/zUid";
import styles from "./EntityStates.module.less";
import { StateOption } from "../../EntityCardStore";

interface PropsEntityStates {
  currentState: React.ReactNode;
  stateOptions: StateOption[];
  onFinish: ((stateId: ZUid) => Promise<void>) | undefined;
  attrNamesMap?: Record<ZUid, string>;
}

export const EntityStates: React.FC<PropsEntityStates> = observer((props) => {
  const { currentState, stateOptions, attrNamesMap, onFinish } = props;
  const [selected, setSelected] = React.useState<ZUid | undefined>();
  const [wait, setWait] = React.useState(false);
  const onSend = () => {
    if (selected) {
      setWait(true);
      onFinish?.(selected)
        .then(() =>
          notification.success({
            message: t("State change performed"),
          }),
        )
        .catch(onError)
        .finally(() => setWait(false));
    }
  };

  const options = stateOptions.map(({ unfilledAttrs, label, ...rest }) => {
    const disabled = !!unfilledAttrs?.length;
    const tooltipAttrs = unfilledAttrs
      ?.map((attrId) => `"${attrNamesMap?.[attrId] ?? attrId}"`)
      .join(", ");
    return {
      ...rest,
      disabled,
      label: (
        <Space>
          {label}
          {disabled && (
            <Tooltip
              title={t(
                "The attributes are mandatory when transitioning to this state",
                { attrs: tooltipAttrs },
              )}
            >
              <QuestionCircleOutlined />
            </Tooltip>
          )}
        </Space>
      ),
    };
  });

  React.useEffect(() => {
    const selectedOption = options.find(({ value }) => value === selected);
    if (!selectedOption || selectedOption.disabled) setSelected(undefined);
  }, [options]);

  return (
    <div className={styles.box}>
      <div>
        <div className={styles.label}>{t("Current state")}:</div>
        {currentState}
      </div>
      {options.length > 0 && (
        <div>
          <div className={styles.label}>{t("Change state")}:</div>
          <div className={styles.form}>
            <Radio.Group
              options={options}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            />
            <Button
              type="primary"
              size="small"
              disabled={!selected}
              onClick={onSend}
              loading={wait}
            >
              {t("Change")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
